// Quality Assurance tab (B6) — TanStack Query hooks over qaReviewsApi.
// Mirrors the usePortfolioAdmin conventions (stable key factory; mutations
// invalidate the touched queries; toasts/errors live in the components).

import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { qaReviewsApi, qaScopeAreasApi } from "@/api/portfolioAdmin/qaReviewsApi";
import type {
  CreateLoanQaScopeAreaRequest,
  LoanQaOverviewDto,
  LoanQaScopeAreaCatalogItemDto,
  UpdateLoanQaReviewRequest,
  UpdateLoanQaScopeAreaCatalogRequest,
} from "@/api/portfolioAdmin/qaReviewsApi";
import { loanReportsKey } from "./useLoanReports";
import { invalidateLoanCalendar, invalidateLoanHealth } from "./invalidation";

export function qaOverviewKey(loanId: string) {
  return ["portfolio-admin", "loan", loanId, "qa-reviews"] as const;
}

export const qaCatalogKeys = {
  all: () => ["portfolio-admin", "qa-scope-areas"] as const,
  list: (includeInactive: boolean) =>
    ["portfolio-admin", "qa-scope-areas", { includeInactive }] as const,
};

export function useLoanQaOverview(loanId: string | undefined) {
  return useQuery<LoanQaOverviewDto>({
    queryKey: qaOverviewKey(loanId ?? ""),
    queryFn: () => qaReviewsApi.overview(loanId!),
    enabled: !!loanId,
  });
}

function useQaMutation<TArgs>(
  loanId: string,
  fn: (args: TArgs) => Promise<unknown>,
  options?: { alsoReports?: boolean },
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    // Return the promise so mutateAsync resolves AFTER the refetch — the
    // QA tab opens the new review's drawer off fresh overview data. The QA
    // cadence is calendar-backed and badges through the health endpoint.
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: qaOverviewKey(loanId) }),
        invalidateLoanCalendar(qc, loanId),
        invalidateLoanHealth(qc, loanId),
        options?.alsoReports
          ? qc.invalidateQueries({ queryKey: loanReportsKey(loanId, "Qa") })
          : Promise.resolve(),
      ]),
  });
}

export function useStartQaReview(loanId: string) {
  return useQaMutation(loanId, () => qaReviewsApi.start(loanId));
}

export function useUpdateQaReview(loanId: string) {
  return useQaMutation(
    loanId,
    (args: { reviewId: string; body: UpdateLoanQaReviewRequest }) =>
      qaReviewsApi.update(args.reviewId, args.body),
  );
}

export function useCompleteQaReview(loanId: string) {
  return useQaMutation(loanId, (reviewId: string) =>
    qaReviewsApi.complete(reviewId),
  );
}

export function useCreateQaFollowUpTask(loanId: string) {
  return useQaMutation(
    loanId,
    (args: { reviewId: string; findingId: string }) =>
      qaReviewsApi.createFollowUpTask(args.reviewId, args.findingId),
  );
}

export function useGenerateQaReport(loanId: string) {
  return useQaMutation(
    loanId,
    (reviewId: string) => qaReviewsApi.generateReport(reviewId),
    { alsoReports: true },
  );
}

export function useCertifyQaReport(loanId: string) {
  return useQaMutation(
    loanId,
    (reportId: string) => qaReviewsApi.certifyReport(reportId),
    { alsoReports: true },
  );
}

export function useFileQaReport(loanId: string) {
  return useQaMutation(
    loanId,
    (reportId: string) => qaReviewsApi.fileReport(reportId),
    { alsoReports: true },
  );
}

// ----- scope-area catalog (admin) ------------------------------------------

export function useQaScopeAreas(includeInactive = false, enabled = true) {
  return useQuery<LoanQaScopeAreaCatalogItemDto[]>({
    queryKey: qaCatalogKeys.list(includeInactive),
    queryFn: () => qaScopeAreasApi.list(includeInactive),
    enabled,
  });
}

// A catalog edit changes the area lineup every loan's Draft review tops up to,
// so invalidate the catalog reads here; loan QA overviews self-heal on their
// next fetch (the server re-seeds OnByDefault areas on read).
function useQaCatalogMutation<TArgs>(fn: (args: TArgs) => Promise<unknown>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => qc.invalidateQueries({ queryKey: qaCatalogKeys.all() }),
  });
}

export function useCreateQaScopeArea() {
  return useQaCatalogMutation((body: CreateLoanQaScopeAreaRequest) =>
    qaScopeAreasApi.create(body),
  );
}

export function useUpdateQaScopeArea() {
  return useQaCatalogMutation(
    (args: { id: string; body: UpdateLoanQaScopeAreaCatalogRequest }) =>
      qaScopeAreasApi.update(args.id, args.body),
  );
}

export function useReorderQaScopeAreas() {
  return useQaCatalogMutation((orderedIds: string[]) =>
    qaScopeAreasApi.reorder(orderedIds),
  );
}
